import { inject, Injectable, signal } from '@angular/core';
import { Mesh, ShaderMaterial } from 'three';
import { SHARP_EDGES_SERVICE } from '@infinitform/platform-core';
import { LocalStorageSetting, SessionService } from './session.service';
import { thinDesignThresholdMm } from '../engine/thin-regions';
import {
  calculateMinimumFeatureSize,
  calculateVoxelEdge
} from '../features/flow/optimization/utils/voxel-calculations.util';

/**
 * Shared display settings service for all editor types.
 * Handles zoom, grid, dimensions, helpers, clipping, tooltips, and other
 * canvas-related display controls.
 *
 * Must be provided at the component level (not root) since it depends
 * on the engine instance from SessionService which is per-editor.
 */
@Injectable()
export class DisplaySettingsService {
  private session = inject(SessionService);
  private sharpEdgesService = inject(SHARP_EDGES_SERVICE, { optional: true });

  /** Whether any design part has sharp edges on file; false until {@link initSharpEdges} runs. */
  readonly sharpEdgesAvailable = signal(false);
  /** Whether non-design parts cover some of the design parts; false until {@link initDesignOverlap} runs. */
  readonly designOverlapAvailable = signal(false);
  /** How far the design-overlap overlay is built, 0 to 1; null when it is not being built. */
  readonly designOverlapProgress = signal<number | null>(null);
  /** Whether there is a design part to check for thin regions; false until {@link initThinRegions} runs. */
  readonly thinRegionsAvailable = signal(false);
  /** How far the thin-regions overlay is built, 0 to 1; null when it is not being built. */
  readonly thinRegionsProgress = signal<number | null>(null);

  // ── Zoom ──────────────────────────────────────────────────────────────

  zoomIn(): void {
    const engine = this.session.engine;
    if (engine?.controls) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls = engine.controls as any;
      if (controls.dollyIn) {
        controls.dollyIn(1.3);
        controls.update();
      } else if (engine.camera.zoom) {
        engine.camera.zoom *= 1.3;
        engine.camera.updateProjectionMatrix();
      }
    }
  }

  zoomOut(): void {
    const engine = this.session.engine;
    if (engine?.controls) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const controls = engine.controls as any;
      if (controls.dollyOut) {
        controls.dollyOut(1.3);
        controls.update();
      } else if (engine.camera.zoom) {
        engine.camera.zoom /= 1.3;
        engine.camera.updateProjectionMatrix();
      }
    }
  }

  // ── Grid ──────────────────────────────────────────────────────────────

  toggleGridVisible(value?: boolean): void {
    const engine = this.session.engine!;
    if (value !== undefined) {
      engine.gridVisible = value;
    } else {
      engine.gridVisible = !engine.gridVisible;
    }
    this.session.setSetting(
      LocalStorageSetting.LAYOUT_GRID,
      engine.gridVisible ? 'yes' : 'no'
    );
  }

  // ── Sharp edges (admin) ───────────────────────────────────────────────

  /**
   * Starts reading the design parts' sharp edges, as measured at import, and
   * keeps {@link sharpEdgesAvailable} up to date. Call it only for users who
   * may see the overlay: nothing is read before it.
   */
  initSharpEdges(): void {
    const engine = this.session.engine;
    const projectId = this.session.project_id;
    const service = this.sharpEdgesService;
    this.sharpEdgesAvailable.set(false);
    if (!engine || !projectId || !service) return;
    engine.sharpEdges.onAvailabilityChange = (available) => this.sharpEdgesAvailable.set(available);
    engine.sharpEdges.setLoader((partId) => service.load(projectId, partId));
  }

  /** Shows or hides the design parts' sharp edges. */
  toggleSharpEdges(visible: boolean): void {
    const engine = this.session.engine;
    if (!engine) return;
    if (visible) void engine.sharpEdges.show();
    else engine.sharpEdges.hide();
  }

  // ── Design overlap (admin) ────────────────────────────────────────────

  /**
   * Starts measuring where non-design parts cover the design parts, and keeps
   * {@link designOverlapAvailable} up to date. Call it only for users who may
   * see the overlay: nothing is measured for it before.
   */
  initDesignOverlap(): void {
    const engine = this.session.engine;
    this.designOverlapAvailable.set(false);
    this.designOverlapProgress.set(null);
    if (!engine) return;
    engine.designOverlap.onAvailabilityChange = (available) => this.designOverlapAvailable.set(available);
    engine.designOverlap.onProgress = (progress) => this.designOverlapProgress.set(progress);
    engine.designOverlap.enable();
  }

  /** Shows or hides where non-design parts cover the design parts. */
  toggleDesignOverlap(visible: boolean): void {
    const engine = this.session.engine;
    if (!engine) return;
    if (visible) engine.designOverlap.show();
    else engine.designOverlap.hide();
  }

  // ── Thin regions (admin) ──────────────────────────────────────────────

  /**
   * Starts tracking whether there are design parts to check for thin regions,
   * and keeps {@link thinRegionsAvailable} up to date. Call it only for users
   * who may see the overlay: nothing is measured for it before.
   */
  initThinRegions(): void {
    const engine = this.session.engine;
    this.thinRegionsAvailable.set(false);
    this.thinRegionsProgress.set(null);
    if (!engine) return;
    engine.thinRegions.onAvailabilityChange = (available) => this.thinRegionsAvailable.set(available);
    engine.thinRegions.onProgress = (progress) => this.thinRegionsProgress.set(progress);
    engine.thinRegions.enable(() => this.thinRegionThresholdMm());
  }

  /** Shows or hides the thin regions the thin-design warning reports. */
  toggleThinRegions(visible: boolean): void {
    const engine = this.session.engine;
    if (!engine) return;
    if (visible) engine.thinRegions.show();
    else engine.thinRegions.hide();
  }

  /** The thin-design warning's threshold for the current resolution, in mm. */
  private thinRegionThresholdMm(): number {
    const engine = this.session.engine;
    if (!engine) return 0;
    const project = this.session.project;
    const design = engine.objectsDesign;
    // Before the Run step fills it in, the minimum feature size is the one
    // the run would take.
    const featureSizeMm = project.minimumFeatureSize || calculateMinimumFeatureSize(project.resolution, design);
    return thinDesignThresholdMm(
      featureSizeMm,
      calculateVoxelEdge(project.resolution, design, project.userDefinedResolution)
    );
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  toggleHelpers(next?: boolean): boolean {
    const engine = this.session.engine!;
    const { drillBits, symmetryPlanes, ucsFrame } = engine;

    const shouldShow = typeof next === 'boolean' ? next : !engine.helpersVisible;

    if (!shouldShow) {
      [drillBits?.x1, drillBits?.y1, drillBits?.z1, drillBits?.x2, drillBits?.y2, drillBits?.z2].forEach((obj) =>
        obj?.removeFromParent()
      );
      [symmetryPlanes?.xy, symmetryPlanes?.xz, symmetryPlanes?.yz].forEach((plane) => plane?.removeFromParent());
      engine.hideArrow();
    } else {
      ucsFrame.add(drillBits.x1!, drillBits.y1!, drillBits.z1!, drillBits.x2!, drillBits.y2!, drillBits.z2!);
      ucsFrame.add(symmetryPlanes?.xy!, symmetryPlanes?.xz!, symmetryPlanes?.yz!);
      engine.showArrow();
    }

    engine.helpersVisible = shouldShow;

    this.session.setSetting(
      LocalStorageSetting.LAYOUT_HELPERS,
      this.session.engine?.helpersVisible === true ? 'yes' : 'no'
    );

    return engine.helpersVisible;
  }

  // ── Dimensions ────────────────────────────────────────────────────────

  toggleDimensionsVisible(): void {
    this.session.engine?.toggleDimensionsHelper();

    this.session.setSetting(
      LocalStorageSetting.LAYOUT_DIMENSIONS,
      this.session.engine?.dimensionsHelperVisible === true ? 'yes' : 'no'
    );
  }

  // ── Clipping ──────────────────────────────────────────────────────────

  clippingVisualisationChange(): void {
    setTimeout(() => {
      const engine = this.session.engine;
      if (engine) {
        engine.renderer.localClippingEnabled = engine.clippingEnabled;

        engine.scene.traverse((o) => {
          const isClippingHelper =
            o instanceof Mesh &&
            (o.name.endsWith('_SurfaceModel') ||
              o.name.endsWith('_BackSideModel') ||
              o.name.endsWith('_StencilFront') ||
              o.name.endsWith('_StencilBack') ||
              o.name === 'PlaneMeshX' ||
              o.name === 'PlaneMeshY' ||
              o.name === 'PlaneMeshZ');
          if (engine && isClippingHelper) {
            o.visible = engine.clippingEnabled;
          }
        });
      }
    });
  }

  clippingChange(value: number, type: 'x' | 'y' | 'z'): void {
    this.session.engine?.clipper.forEach((c) => c.setPosition(value, type));
  }

  clippingParametricChange(_value: number, _type: 'x' | 'y' | 'z'): void {
    // Reserved for future parametric clipping
  }

  // ── Load case / FEA ───────────────────────────────────────────────────

  toggleLoadCaseHighlight(value?: boolean): void {
    this.session.engine!.toggleLoadCaseHighlight(value);
  }

  toggleReactionForces(): void {
    this.session.engine!.toggleReactionForces();
  }

  animateTexture(_value?: boolean): void {
    setTimeout(() => {
      const feaMeshOrganic = this.session.engine?.infoGroup.children.find(
        (c) => c.userData['fea_type'] === 'organic'
      ) as Mesh;
      const feaMeshPrismatic = this.session.engine?.infoGroup.children.find(
        (c) => c.userData['fea_type'] === 'prismatic'
      ) as Mesh;

      if (feaMeshOrganic) {
        const material = feaMeshOrganic.material as ShaderMaterial;
        if (material && material.uniforms && material.uniforms['animate']) {
          material.uniforms['animate'].value = this.session.engine!.animateTexture === true ? 1 : 0;
        }
      }

      if (feaMeshPrismatic) {
        const material = feaMeshPrismatic.material as ShaderMaterial;
        if (material && material.uniforms && material.uniforms['animate']) {
          material.uniforms['animate'].value = this.session.engine!.animateTexture === true ? 1 : 0;
        }
      }
    });
  }

  currentDisplacementVisible(): string {
    if (this.session.project.flowVersion === 2) {
      const mesh = this.session.engine!.infoGroup.children.find(
        (c) => c.userData['fea_type'] === this.session.engine?.currentFEAOutput
      );
      for (const type of ['t', 'x', 'y', 'z', 's']) {
        if (mesh) {
          const meshVisible = mesh.visible;
          const meshCurrentDisplacementTypeMatch = mesh.userData['currentDisplacementType'] === type;
          if (meshVisible && meshCurrentDisplacementTypeMatch) return type;
        }
      }
    } else {
      for (const type of ['t', 'x', 'y', 'z', 's']) {
        const visible = this.session.engine!.infoGroup.getObjectByName(
          `lc_${this.session.engine!.currentLoadCase}_${type}`
        )!.visible;
        if (visible === true) return type;
      }
    }
    return 't';
  }

  // ── Tooltip helpers with dynamic keyboard shortcuts ───────────────────

  getShortcutKey(id: string): string {
    return this.session.keyboardShortcuts.getKey(id) || '';
  }

  getHomeViewTooltip(): string {
    const key = this.getShortcutKey('home-view');
    return key ? `Reset home view (${key})` : 'Reset home view';
  }

  getFitAllTooltip(): string {
    const key = this.getShortcutKey('fit-all');
    return key ? `Fit all in view (${key})` : 'Fit all in view';
  }

  // The Grid and Dimensions checkboxes are labelled, so their tooltip only adds the shortcut.
  getToggleGridTooltip(): string {
    const key = this.getShortcutKey('toggle-grid');
    return key ? `Toggle grid (${key})` : '';
  }

  getToggleDimensionsTooltip(): string {
    const key = this.getShortcutKey('toggle-dimensions');
    return key ? `Toggle dimensions (${key})` : '';
  }
}
